import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Car, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isLogin ? '/admin/login' : '/admin/register';
      const payload = isLogin 
        ? { username: formData.username, password: formData.password }
        : formData;
      
      const res = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('admin_token', res.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(res.data.admin));
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-[#2C4A3B] rounded-full flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-[#2C4A3B]">YesApproved</span>
          </a>
          <h1 className="font-heading text-3xl font-bold text-[#2C4A3B] mb-2">
            {isLogin ? 'Admin Login' : 'Create Admin Account'}
          </h1>
          <p className="text-[#5A5A5A]">
            {isLogin ? 'Sign in to manage your dealership' : 'Register a new admin account'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E0D6] p-8 shadow-sm" data-testid="login-form-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-[#2C4A3B] font-medium">Username</Label>
              <Input
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Enter your username"
                className="input-custom mt-2"
                required
                data-testid="input-username"
              />
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="email" className="text-[#2C4A3B] font-medium">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="admin@example.com"
                  className="input-custom mt-2"
                  required
                  data-testid="input-email"
                />
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-[#2C4A3B] font-medium">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className="input-custom pr-12"
                  required
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#2C4A3B] transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary"
              data-testid="submit-login-btn"
            >
              {isLoading ? (
                'Please wait...'
              ) : isLogin ? (
                <>
                  <LogIn className="mr-2 w-5 h-5" /> Sign In
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 w-5 h-5" /> Create Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#C07756] hover:text-[#A66548] font-medium transition-colors"
              data-testid="toggle-mode-btn"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
            </button>
          </div>
        </div>

        <p className="text-center text-[#5A5A5A] text-sm mt-6">
          <a href="/" className="hover:text-[#C07756] transition-colors">← Back to main site</a>
        </p>
      </div>
    </div>
  );
}
